import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { useQueryClient } from "@tanstack/react-query";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { useCreateUser } from "../../../hooks/useCreateUser";
import { useRoles } from "../../../hooks/useGetRoles";
import { useColors } from "../../../hooks/COLORS/useColors";

const NewUser = ({ showNewUser, setShowNewUser }) => {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        role: null,
        couleur: null,
        couleurHex: "#cccccc"
    });

    const { mutate: createUser, isPending } = useCreateUser();
    const { data: rolesData } = useRoles();
    const { data: colorsData } = useColors();

    const roles = rolesData?.data?.map(r => ({ value: r.id, label: r.name })) || [];
    const colors = colorsData?.data?.map(c => ({ 
        value: c.id, 
        label: c.name, 
        hex: c.hex 
    })) || [];

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleColorChange = (opt) => {
        setFormData(prev => ({ 
            ...prev, 
            couleur: opt?.value ?? null,
            couleurHex: opt?.hex ?? "#cccccc"
        }));
    };

    const handleClose = () => {
        setFormData({ nom: "", prenom: "", email: "", telephone: "", role: null, couleur: null, couleurHex: "#cccccc" });
        setShowNewUser(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { couleurHex, ...dataToSend } = formData;
        
        createUser(dataToSend, {
            onSuccess: () => {
                queryClient.invalidateQueries(['users']);
                handleClose();
            },
        });
    };
    

    return (
        <Modal show={showNewUser} onHide={handleClose} size="lg">
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5 className="modal-title text-utilisateur">Nouvel utilisateur</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-3">
                        <Col>
                            <Form.Control
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Form.Control
                                type="text"
                                name="nom"
                                placeholder="Nom"
                                value={formData.nom}
                                onChange={handleChange}
                                required
                            />
                        </Col>
                        <Col>
                            <Form.Control
                                type="text"
                                name="prenom"
                                placeholder="Prénom"
                                value={formData.prenom}
                                onChange={handleChange}
                                required
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Form.Control
                                type="tel"
                                name="telephone"
                                placeholder="Téléphone"
                                value={formData.telephone}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Form.Group as={Col}>
                            <Form.Label className="text-muted">Rôle</Form.Label>
                            <Select
                                options={roles}
                                styles={customSelectStyles}
                                placeholder="Sélectionner un rôle"
                                onChange={(opt) => setFormData(prev => ({ ...prev, role: opt?.value ?? null }))}
                            />
                        </Form.Group>
                    </Row>
                    
                    <Row>
                        <Form.Group className='mb-3' as={Col}>
                            <Form.Label className='text-muted'>Choisissez une couleur</Form.Label>
                            <Select
                                options={colors}
                                styles={customSelectStyles}
                                placeholder="Sélectionner..."
                                onChange={handleColorChange}
                                formatOptionLabel={(opt) => (
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            backgroundColor: opt.hex,
                                            border: "1px solid #dee2e6"
                                        }} />
                                        {opt.label}
                                    </div>
                                )}
                            />
                        </Form.Group>
                        
                        <Form.Group className='mb-3' as={Col}>
                            <Form.Label className='text-muted'>Prévisualisation</Form.Label>
                            <div 
                                style={{ 
                                    width: '100%', 
                                    height: '38px', 
                                    backgroundColor: formData.couleurHex, 
                                    border: '1px solid #ced4da', 
                                    borderRadius: '0.375rem',
                                    transition: 'background-color 0.2s ease-in-out'
                                }} 
                            />
                        </Form.Group>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isPending}>
                        Annuler
                    </Button>
                    <Button variant="primary" type="submit" disabled={isPending}>
                        {isPending ? "Création..." : "Créer"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default NewUser;